import { useQuery } from '@tanstack/react-query';
import { RefreshCw, Server, Clock } from 'lucide-react';
import { getServiceHealth } from '../../services/healthService';
import { getMarketProvidersHealth } from '../../services/perfService';
import type { ProviderHealth } from '../../services/perfService';
import type { DependencyStatus, MonitorHealthResponse } from '../../types/health';

// ── Status pill styling ────────────────────────────────────────────────────────

const STATUS_STYLE: Record<DependencyStatus, { label: string; pill: string; dot: string }> = {
  ok:          { label: 'OK',         pill: 'bg-green-900/30 text-green-400 border-green-800/50',  dot: 'bg-green-400' },
  disabled:    { label: 'Disabled',   pill: 'bg-slate-800/50 text-slate-400 border-slate-700/50',  dot: 'bg-slate-400' },
  stale:       { label: 'Stale',      pill: 'bg-amber-900/30 text-amber-400 border-amber-800/50',  dot: 'bg-amber-400' },
  'no-workers':{ label: 'No Workers', pill: 'bg-amber-900/30 text-amber-400 border-amber-800/50',  dot: 'bg-amber-400' },
  error:       { label: 'Error',      pill: 'bg-red-900/30   text-red-400   border-red-800/50',    dot: 'bg-red-400'   },
  unreachable: { label: 'Unreachable',pill: 'bg-red-900/30   text-red-400   border-red-800/50',    dot: 'bg-red-400'   },
};

function StatusPill({ status }: { status: DependencyStatus }) {
  const s = STATUS_STYLE[status] ?? STATUS_STYLE.error;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${s.pill}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

// ── Service cards ──────────────────────────────────────────────────────────────

const SERVICE_LABELS: Record<keyof MonitorHealthResponse['dependencies'], string> = {
  postgres:      'PostgreSQL',
  pgbouncer:     'PgBouncer',
  redis:         'Redis',
  worker:        'Worker',
  push_consumer: 'Push Consumer',
  celery:        'Celery',
  backend:       'Backend API',
  ea_sidecar:    'EA Sidecar',
  eodhd_mcp:     'EODHD MCP',
  brave_mcp:     'Brave MCP',
  flower:        'Flower',
  expo_push:     'Expo Push',
  frontend:      'Frontend',
  iress:         'IRESS',
  eodhd_api:     'EODHD API',
  marome_mcp:       'My Investments MCP',
  tradingview_mcp:  'TradingView MCP',
  payments:         'Payments',
  gatekeeper:       'Gatekeeper',
  media_processor:  'Media Processor',
};

function ServiceCard({ name, status }: { name: string; status: DependencyStatus }) {
  const borderClass =
    status === 'ok'       ? 'border-slate-700/50' :
    status === 'disabled' ? 'border-slate-700/30' :
                            'border-red-800/40';
  return (
    <div className={`bg-slate-800/50 border rounded-lg p-4 flex flex-col gap-2 ${borderClass}`}>
      <p className="text-sm font-medium text-slate-300">{name}</p>
      <StatusPill status={status} />
    </div>
  );
}

// ── Provider latency card (Option C) ──────────────────────────────────────────

function fetchMsColor(ms: number): string {
  if (ms < 500)  return 'text-green-400';
  if (ms < 2000) return 'text-amber-400';
  return 'text-red-400';
}

function ProviderLatencyCard({ p }: { p: ProviderHealth }) {
  const isError = p.status === 'error';
  const isClosed = p.status === 'market-closed';
  return (
    <div className={`bg-slate-800/50 border rounded-lg p-4 space-y-2 ${isError ? 'border-red-800/40' : isClosed ? 'border-slate-700/30' : 'border-slate-700/50'}`}>
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-slate-300">{p.provider}</p>
        <span className={`text-xs font-medium ${isError ? 'text-red-400' : isClosed ? 'text-slate-500' : 'text-green-400'}`}>
          {p.status}
        </span>
      </div>
      <p className="text-xs text-slate-500">{p.exchange}</p>
      {p.fetch_ms != null ? (
        <div className="flex items-center gap-1.5">
          <Clock className="w-3 h-3 text-slate-500" />
          <span className={`text-sm font-bold ${fetchMsColor(p.fetch_ms)}`}>{p.fetch_ms}ms</span>
          <span className="text-xs text-slate-600">last fetch</span>
        </div>
      ) : (
        <p className="text-xs text-slate-600 italic">no timing data yet</p>
      )}
      {isError && p.detail && (
        <p className="text-xs text-red-400 truncate" title={p.detail}>{p.detail}</p>
      )}
    </div>
  );
}

// ── Relative time ──────────────────────────────────────────────────────────────

function relativeTime(iso: string): string {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 5)  return 'just now';
  if (diff < 60) return `${diff}s ago`;
  const m = Math.floor(diff / 60);
  if (m < 60)   return `${m}m ago`;
  return `${Math.floor(m / 60)}h ago`;
}

// ── Main page ──────────────────────────────────────────────────────────────────

export default function ServicesStatus() {
  const { data, isFetching, error, refetch } = useQuery<MonitorHealthResponse>({
    queryKey: ['service-health'],
    queryFn: getServiceHealth,
    refetchInterval: 30_000,
    retry: 1,
  });

  const { data: providers, refetch: refetchProviders } = useQuery({
    queryKey: ['market-providers-health'],
    queryFn: getMarketProvidersHealth,
    refetchInterval: 30_000,
    retry: 1,
  });

  const overall = data?.status ?? 'degraded';
  const isHealthy = overall === 'healthy';

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Server className="w-5 h-5 text-teal-400" />
          <h1 className="text-xl font-semibold text-slate-100">Service Health</h1>
          {data && (
            <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${
              isHealthy
                ? 'bg-green-900/30 text-green-400 border-green-800/50'
                : 'bg-amber-900/30 text-amber-400 border-amber-800/50'
            }`}>
              {isHealthy ? '✓ Healthy' : '⚠ Degraded'}
            </span>
          )}
        </div>
        <button
          onClick={() => { refetch(); refetchProviders(); }}
          disabled={isFetching}
          className="flex items-center gap-2 px-3 py-1.5 rounded-md text-sm text-slate-300 bg-slate-700/50 hover:bg-slate-700 border border-slate-600/50 disabled:opacity-50 transition-colors"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Summary bar */}
      {data && (
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-3 flex flex-wrap gap-4 text-sm text-slate-400">
          <span><span className="text-slate-300 font-medium">{data.service}</span></span>
          <span>v{data.version}</span>
          <span className="ml-auto">Last checked: {relativeTime(data.checked_at)}</span>
        </div>
      )}

      {/* Error state */}
      {error && !data && (
        <div className="bg-red-900/20 border border-red-800/40 rounded-lg p-4 text-sm text-red-400">
          Unable to reach health endpoint. The backend may be down.
        </div>
      )}

      {/* Service cards */}
      {data && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {(Object.entries(data.dependencies) as [keyof MonitorHealthResponse['dependencies'], DependencyStatus][]).map(
            ([key, status]) => (
              <ServiceCard
                key={key}
                name={SERVICE_LABELS[key]}
                status={status}
              />
            ),
          )}
        </div>
      )}

      {/* Market data provider latency (Option C) */}
      {providers && (
        <div>
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-2">
            <Clock className="w-3.5 h-3.5" /> Market Data Feed Latency
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <ProviderLatencyCard p={providers.iress} />
            <ProviderLatencyCard p={providers.eodhd} />
          </div>
        </div>
      )}

      {/* Loading skeleton */}
      {!data && !error && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-4 h-20 animate-pulse" />
          ))}
        </div>
      )}
    </div>
  );
}
